import NextImage from 'next/image'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import type { DialogProps } from '@wartek-id/dialog-v1'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { useToast } from '@wartek-id/toast'

import {
  useAPIGetFollowUpDocument,
  useAPIGetFollowUpReflectionDocument,
  useAPIPelaksanaanProgress,
  useAPIUpsertFollowUpReflectionDocument,
  API_GET_FOLLOW_UP_DOCUMENT_QUERY_KEY,
  API_PATH_PELAKSANAAN_PROGRESS,
  API_PATH_GET_FOLLOW_UP_REFLECTION_DOCUMENT,
} from 'api/kinerja/useAPIKinerja'
import {
  SectionCard,
  SectionCardHeader,
  SectionCardBody,
  SectionCardFooter,
} from 'app/KinerjaShared/components/SectionCard'
import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'
import { SaveToDraftDialog } from 'app/KinerjaShared/components/SaveToDraftDialog'
import { SubmitConfirmationDialog } from 'app/KinerjaPage/pages/PelaksanaanV2/components/SubmitConfirmationDialog'
import { FOLLOW_UP_REFLECTION_CONTENT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/reflection'
import { DUKUNGAN_UPAYA_TINDAK_LANJUT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/upayaTindakLanjut'
import { PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import useBeforePopState from 'app/KinerjaShared/hooks/useBeforePopState'
import { generateErrorMessage, getErrorCode } from 'app/KinerjaShared/utils/api'
import { PageGuardGpkPelaksanaanStatus } from 'app/KinerjaGpkPage/components/PageGuardGpkPelaksanaanStatus'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { useSafeBackNavigation } from 'app/KinerjaShared/hooks/useSafeBackNavigation'
import { PRAKTIK_KINERJA_STEPS } from 'app/KinerjaGpkPage/constants/praktik-kinerja-steps'

import type { CustomError } from 'utils/customError'
import type {
  IFollowUpReflectionDocumentForm,
  IFollowUpTargetItem,
  TStatePelaksanaan,
} from 'types/kinerja-pelaksanaan'
import type { MultiPeriodPayload } from 'types/kinerja'

type ActiveDialogTypes =
  | 'BELAJAR_LAINNYA'
  | 'BELAJAR_RGTK'
  | 'SAVE_TO_DRAFT'
  | 'SUBMIT'
  | 'NONE'

type RefleksiEvaluasiTindakLanjutProps = {
  periodParams?: MultiPeriodPayload
  ptkId?: string
}

const STEP: TStatePelaksanaan = 'DOCUMENT_REFLECTION_FOLLOW_UP'

export function RefleksiEvaluasiTindakLanjut({
  periodParams,
  ptkId,
}: RefleksiEvaluasiTindakLanjutProps) {
  const router = useRouter()
  const toast = useToast()
  const queryClient = useQueryClient()
  const upsertMutation = useAPIUpsertFollowUpReflectionDocument({
    useErrorBoundary: false,
  })

  const [activeDialog, setActiveDialog] = useState<ActiveDialogTypes>('NONE')

  const { handleBack } = useSafeBackNavigation({
    fallbackRoute: `${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`,
  })

  // -----------------------------------------
  // Resolve period params
  // -----------------------------------------
  const { periodNo, periodYear } = usePeriodContext()

  const resolvedPeriodParams = useMemo(() => {
    if (periodParams) return periodParams

    // Default with current period
    return {
      skpPeriod: periodNo,
      skpYear: periodYear,
    }
  }, [periodParams, periodNo, periodYear])

  const formMethods = useForm<IFollowUpReflectionDocumentForm>({
    defaultValues: {
      additionalAchievement: '',
      additionalChallenge: '',
      additionalImprovementPlan: '',
      additionalInspiration: '',
    },
  })

  // -----------------------------------------
  // Get Pelaksanaan Progress data
  // -----------------------------------------
  const { data: dataPelaksanaanProgress } = useAPIPelaksanaanProgress({
    ptkId,
    tipe: 'GPK',
    periodParams: {
      skp_period: resolvedPeriodParams.skpPeriod,
      skp_year: resolvedPeriodParams.skpYear,
    },
    opts: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const isEvaluated =
    dataPelaksanaanProgress?.data?.evaluationDocumentState === 'READY'

  // -----------------------------------------
  // Get Rencana Tindak Lanjut data
  // -----------------------------------------
  const { data: followUpDocumentData, isLoading: isLoadingFollowUpDocument } =
    useAPIGetFollowUpDocument({
      params: { ptkId, tipe: 'GPK' },
      periodParams: {
        skp_period: resolvedPeriodParams.skpPeriod,
        skp_year: resolvedPeriodParams.skpYear,
      },
      options: {
        useErrorBoundary: true,
        staleTime: 60 * 60 * 1000, // 1 hour
        retry: false,
      },
    })

  // -----------------------------------------
  // Get Refleksi dan Evaluasi Tindak Lanjut data
  // -----------------------------------------
  const {
    isLoading: isLoadingFollowUpReflectionDocument,
    data: followUpReflectionDocumentData,
  } = useAPIGetFollowUpReflectionDocument({
    params: {
      ptkId,
      tipe: 'GPK',
    },
    periodParams: {
      skp_period: resolvedPeriodParams.skpPeriod,
      skp_year: resolvedPeriodParams.skpYear,
    },
    options: {
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
      retry: false,
    },
  })

  const isSubmitted =
    followUpReflectionDocumentData?.data?.state === 'SUBMITTED'

  // -----------------------------------------
  // Resolve Dukungan Tindak Lanjut
  // -----------------------------------------
  const validatedAdditionalSupportNeed = useMemo(() => {
    if (!followUpDocumentData) return '-'

    const additionalSupportNeed = `${followUpDocumentData?.data?.detail?.additionalSupportNeed}`

    return DUKUNGAN_UPAYA_TINDAK_LANJUT.includes(additionalSupportNeed)
      ? additionalSupportNeed
      : `Lainnya: ${additionalSupportNeed}`
  }, [followUpDocumentData])

  // -----------------------------------------
  // Check if Pilihan Belajar RGTK should be displayed
  // -----------------------------------------
  const hasBelajarRuangGtk = useMemo(() => {
    if (!followUpDocumentData?.data?.detail) return false
    const { followUpTargets, supportNeed } = followUpDocumentData.data.detail
    return !!followUpTargets?.length && !!supportNeed
  }, [followUpDocumentData])

  // -----------------------------------------
  // Other derived values
  // -----------------------------------------
  const isReadOnly = isSubmitted || isEvaluated
  const isSaveDraftDisabled =
    !formMethods.formState.isDirty ||
    upsertMutation.isLoading ||
    isLoadingFollowUpDocument ||
    isLoadingFollowUpReflectionDocument ||
    isEvaluated ||
    isReadOnly
  const isInputDisabled = isSubmitted || isEvaluated || isReadOnly

  const stepTitle = PRAKTIK_KINERJA_STEPS[STEP].title

  // -----------------------------------------
  // Handle actions
  // -----------------------------------------

  const upsertConfig = {
    SUBMITTED: {
      successMessage: `Berhasil kirim ${stepTitle}!`,
      errorMessage: `Gagal kirim ${stepTitle}. Coba lagi`,
      onSuccessNav: () =>
        router.replace(
          `${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`
        ),
    },
    DRAFT: {
      successMessage: 'Berhasil simpan draf!',
      errorMessage: 'Gagal simpan draf. Coba lagi',
      onSuccessNav: handleBack,
    },
  } as const

  const mutate = (
    state: 'SUBMITTED' | 'DRAFT',
    detail: IFollowUpReflectionDocumentForm
  ) => {
    const config = upsertConfig[state]
    upsertMutation.mutate(
      { state, detail, tipe: 'GPK' },
      {
        onSuccess: () => {
          config.onSuccessNav()
          toast({ message: config.successMessage, type: 'success' })

          queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS)
          queryClient.invalidateQueries(API_GET_FOLLOW_UP_DOCUMENT_QUERY_KEY)
          queryClient.invalidateQueries(
            API_PATH_GET_FOLLOW_UP_REFLECTION_DOCUMENT
          )
        },
        onError: (err: CustomError) => {
          toast({
            message: generateErrorMessage(
              config.errorMessage,
              getErrorCode(err)
            ),
          })
        },
        onSettled: () => {
          setActiveDialog('NONE')
        },
      }
    )
  }

  const handleUpsert = (state: 'SUBMITTED' | 'DRAFT') => {
    if (isReadOnly) return
    const values = formMethods.getValues()

    if (state === 'SUBMITTED') {
      mutate('SUBMITTED', values)
    } else {
      mutate(state, values)
    }
  }

  const handleClickSaveDraftButton = () => {
    setActiveDialog('SAVE_TO_DRAFT')
  }

  const handleClickSubmitButton = () => {
    setActiveDialog('SUBMIT')
  }

  const handleClickConfirmSubmitDialog = () => {
    handleUpsert('SUBMITTED')
  }

  // -----------------------------------------
  // Sync form with API data
  // -----------------------------------------
  useEffect(() => {
    if (!!followUpReflectionDocumentData) {
      formMethods.reset(followUpReflectionDocumentData.data?.detail)
    }
  }, [followUpReflectionDocumentData, formMethods])

  // -----------------------------------------
  // Handle guard back navigation for unsaved changes
  // -----------------------------------------
  const handleGuardBackNavigation = useCallback(() => {
    if (isReadOnly) return true

    if (!isSaveDraftDisabled) {
      handleClickSaveDraftButton()

      // Prevent default back navigation
      return false
    }
    // Allow default back navigation
    return true
  }, [isSaveDraftDisabled, isReadOnly])

  // Attach the handler to the browser's history back navigation
  useBeforePopState(router, handleGuardBackNavigation)

  return (
    <div className="min-h-full px-8 pb-8 pt-14 bg-gray-10">
      <div className="max-w-6xl mx-auto">
        <Header title={stepTitle} className="mb-9" onBack={handleBack} />
      </div>

      <PageGuardGpkPelaksanaanStatus
        periodParams={{ skpPeriod: periodNo, skpYear: periodYear }}
        targetStep={STEP}
      >
        {isLoadingFollowUpDocument || isLoadingFollowUpReflectionDocument ? (
          <div className="flex justify-center w-full">
            <Spinner />
          </div>
        ) : (
          <form
            className="max-w-5xl mx-auto"
            onSubmit={formMethods.handleSubmit(handleClickSubmitButton)}
          >
            <section className="p-6 space-y-3 bg-white rounded-lg mb-9">
              <Text className="!text-black" variant="heading-sm" as="h2">
                Langkah-langkah:
              </Text>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Text className="!text-blue-60" variant="action">
                    Lakukan Refleksi
                  </Text>
                  <Text>
                    Capaian dan tantangan yang dialami setelah tindak lanjut.
                  </Text>
                </div>

                <div className="flex space-x-3">
                  <Icon color="informational">arrow_circle_right</Icon>
                  <div>
                    <Text className="!text-blue-60" variant="action">
                      Diskusi dengan Tim Kinerja
                    </Text>
                    <Text>Agar lebih mudah isi {stepTitle}.</Text>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <Icon color="informational">arrow_circle_right</Icon>
                  <div>
                    <Text className="!text-blue-60" variant="action">
                      Lakukan Upaya Perbaikan
                    </Text>
                    <Text>Untuk membantu Anda meningkatkan kinerja.</Text>
                  </div>
                </div>
              </div>
            </section>

            <SectionCard>
              <SectionCardHeader
                variant="light-blue"
                className="flex space-x-4"
              >
                <Text variant="heading-lg" className="flex-1" as="h2">
                  {stepTitle}
                </Text>
              </SectionCardHeader>

              <SectionCardBody className="py-6 space-y-4 px-9">
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <NextImage
                      src="/illustrations/img-assessment-subject-book.png"
                      width="64"
                      height="64"
                    />
                    <Text variant="heading-md" className="flex-1" as="h3">
                      Pilihan Belajar
                    </Text>
                  </div>
                  <div className="flex items-start space-x-9">
                    <div className="flex-grow-0 flex-shrink-0 max-w-full p-3 rounded bg-surface-subdued w-80">
                      <dl>
                        <Text as="dt" variant="body-sm-bold">
                          Tujuan Tindak Lanjut
                        </Text>
                        <Text
                          as="dd"
                          variant="body-sm"
                          className="mb-6 line-clamp-3 overflow-wrap-anywhere"
                        >
                          {followUpDocumentData?.data?.detail
                            ?.additionalFollowUpTarget || '-'}
                        </Text>

                        <Text as="dt" variant="body-sm-bold">
                          Upaya Tindak Lanjut
                        </Text>
                        <Text
                          as="dd"
                          variant="body-sm"
                          className="mb-6 line-clamp-3 overflow-wrap-anywhere"
                        >
                          {followUpDocumentData?.data?.detail
                            ?.additionalFollowUpEffort || '-'}
                        </Text>

                        <Text as="dt" variant="body-sm-bold">
                          Periode Tindak Lanjut
                        </Text>
                        <Text
                          as="dd"
                          variant="body-sm"
                          className="mb-6 line-clamp-3 overflow-wrap-anywhere"
                        >
                          {followUpDocumentData?.data?.detail
                            ?.additionalFollowUpPeriod || '-'}
                        </Text>

                        <Text as="dt" variant="body-sm-bold">
                          Kebutuhan Dukungan (Apa/Siapa)
                        </Text>
                        <Text
                          as="dd"
                          variant="body-sm"
                          className="mb-6 line-clamp-3 overflow-wrap-anywhere"
                        >
                          {validatedAdditionalSupportNeed}
                        </Text>
                      </dl>
                      {!isReadOnly && (
                        <Button
                          size="sm"
                          color="white"
                          aria-haspopup={true}
                          aria-expanded={activeDialog === 'BELAJAR_LAINNYA'}
                          aria-label="Baca selengkapnya Pilihan Belajar"
                          onClick={() => setActiveDialog('BELAJAR_LAINNYA')}
                        >
                          Selengkapnya <Icon>chevron_right</Icon>
                        </Button>
                      )}
                    </div>
                    <div className="w-full space-y-4">
                      <fieldset className="space-y-4">
                        <Text variant="heading-sm">
                          {
                            FOLLOW_UP_REFLECTION_CONTENT.additionalInspiration
                              ?.question
                          }
                        </Text>
                        <div className="space-y-1">
                          <Text
                            as="label"
                            htmlFor="additionalInspiration"
                            color="subdued"
                            aria-hidden={isReadOnly}
                          >
                            {
                              FOLLOW_UP_REFLECTION_CONTENT.additionalInspiration
                                ?.label
                            }
                          </Text>
                          {isReadOnly ? (
                            <>
                              <Text
                                className="overflow-wrap-anywhere"
                                aria-hidden="true"
                              >
                                {formMethods.getValues('additionalInspiration')}
                              </Text>
                              <span className="sr-only">
                                {`${
                                  FOLLOW_UP_REFLECTION_CONTENT
                                    .additionalInspiration?.label
                                }, ${formMethods.getValues(
                                  'additionalInspiration'
                                )}`}
                              </span>
                            </>
                          ) : (
                            <Textarea
                              {...formMethods.register(
                                'additionalInspiration',
                                {
                                  required: {
                                    value: true,
                                    message: 'Wajib diisi',
                                  },
                                  maxLength: 500,
                                }
                              )}
                              isDisabled={isInputDisabled}
                              id="additionalInspiration"
                              placeholder="Ketik di sini"
                              maxLength={500}
                              isInvalid={
                                !!formMethods.formState.errors[
                                  'additionalInspiration'
                                ]
                              }
                              errorMessage={
                                formMethods.formState.errors[
                                  'additionalInspiration'
                                ]?.message
                              }
                            />
                          )}
                        </div>
                      </fieldset>
                      <fieldset className="space-y-4">
                        <Text variant="heading-sm">
                          {
                            FOLLOW_UP_REFLECTION_CONTENT.additionalAchievement
                              ?.question
                          }
                        </Text>
                        <div className="space-y-1">
                          <Text
                            as="label"
                            htmlFor="additionalAchievement"
                            color="subdued"
                            aria-hidden={isReadOnly}
                          >
                            {
                              FOLLOW_UP_REFLECTION_CONTENT.additionalAchievement
                                ?.label
                            }
                          </Text>
                          {isReadOnly ? (
                            <>
                              <Text
                                className="overflow-wrap-anywhere"
                                aria-hidden="true"
                              >
                                {formMethods.getValues('additionalAchievement')}
                              </Text>
                              <span className="sr-only">
                                {`${
                                  FOLLOW_UP_REFLECTION_CONTENT
                                    .additionalAchievement?.label
                                }, ${formMethods.getValues(
                                  'additionalAchievement'
                                )}`}
                              </span>
                            </>
                          ) : (
                            <Textarea
                              {...formMethods.register(
                                'additionalAchievement',
                                {
                                  required: {
                                    value: true,
                                    message: 'Wajib diisi',
                                  },
                                  maxLength: 500,
                                }
                              )}
                              isDisabled={isInputDisabled}
                              id="additionalAchievement"
                              className="w-full"
                              placeholder="Ketik di sini"
                              maxLength={500}
                              isInvalid={
                                !!formMethods.formState.errors[
                                  'additionalAchievement'
                                ]
                              }
                              errorMessage={
                                formMethods.formState.errors[
                                  'additionalAchievement'
                                ]?.message
                              }
                            />
                          )}
                        </div>
                      </fieldset>
                      <fieldset className="space-y-4">
                        <Text variant="heading-sm">
                          {
                            FOLLOW_UP_REFLECTION_CONTENT.additionalChallenge
                              ?.question
                          }
                        </Text>
                        <div className="space-y-1">
                          <Text
                            as="label"
                            htmlFor="additionalChallenge"
                            color="subdued"
                            aria-hidden={isReadOnly}
                          >
                            {
                              FOLLOW_UP_REFLECTION_CONTENT.additionalChallenge
                                ?.label
                            }
                          </Text>
                          {isReadOnly ? (
                            <>
                              <Text
                                className="overflow-wrap-anywhere"
                                aria-hidden="true"
                              >
                                {formMethods.getValues('additionalChallenge')}
                              </Text>
                              <span className="sr-only">
                                {`${
                                  FOLLOW_UP_REFLECTION_CONTENT
                                    .additionalChallenge?.label
                                }, ${formMethods.getValues(
                                  'additionalChallenge'
                                )}`}
                              </span>
                            </>
                          ) : (
                            <Textarea
                              {...formMethods.register('additionalChallenge', {
                                required: {
                                  value: true,
                                  message: 'Wajib diisi',
                                },
                                maxLength: 500,
                              })}
                              isDisabled={isInputDisabled}
                              id="additionalChallenge"
                              placeholder="Ketik di sini"
                              maxLength={500}
                              isInvalid={
                                !!formMethods.formState.errors[
                                  'additionalChallenge'
                                ]
                              }
                              errorMessage={
                                formMethods.formState.errors[
                                  'additionalChallenge'
                                ]?.message
                              }
                            />
                          )}
                        </div>
                      </fieldset>
                      <fieldset className="space-y-4">
                        <Text variant="heading-sm">
                          {
                            FOLLOW_UP_REFLECTION_CONTENT
                              .additionalImprovementPlan?.question
                          }
                        </Text>
                        <div className="space-y-1">
                          <Text
                            as="label"
                            htmlFor="additionalImprovementPlan"
                            color="subdued"
                            aria-hidden={isReadOnly}
                          >
                            {
                              FOLLOW_UP_REFLECTION_CONTENT
                                .additionalImprovementPlan?.label
                            }
                          </Text>
                          {isReadOnly ? (
                            <>
                              <Text
                                className="overflow-wrap-anywhere"
                                aria-hidden="true"
                              >
                                {formMethods.getValues(
                                  'additionalImprovementPlan'
                                )}
                              </Text>
                              <span className="sr-only">
                                {`${
                                  FOLLOW_UP_REFLECTION_CONTENT
                                    .additionalImprovementPlan?.label
                                }, ${formMethods.getValues(
                                  'additionalImprovementPlan'
                                )}`}
                              </span>
                            </>
                          ) : (
                            <Textarea
                              {...formMethods.register(
                                'additionalImprovementPlan',
                                {
                                  required: {
                                    value: true,
                                    message: 'Wajib diisi',
                                  },
                                  maxLength: 500,
                                }
                              )}
                              isDisabled={isInputDisabled}
                              id="additionalImprovementPlan"
                              placeholder="Ketik di sini"
                              maxLength={500}
                              isInvalid={
                                !!formMethods.formState.errors[
                                  'additionalImprovementPlan'
                                ]
                              }
                              errorMessage={
                                formMethods.formState.errors[
                                  'additionalImprovementPlan'
                                ]?.message
                              }
                            />
                          )}
                        </div>
                      </fieldset>
                    </div>
                  </div>
                </div>

                {hasBelajarRuangGtk && (
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <NextImage
                        src="/illustrations/img-smartphone-star.png"
                        width="64"
                        height="64"
                      />
                      <Text variant="heading-md" className="flex-1" as="h3">
                        Pilihan Belajar dari Ruang GTK
                      </Text>
                    </div>
                    <div className="flex items-start space-x-9">
                      <div className="flex-grow-0 flex-shrink-0 max-w-full p-3 rounded bg-surface-subdued w-80">
                        <dl>
                          <Text as="dt" variant="body-sm-bold">
                            Tujuan Tindak Lanjut
                          </Text>
                          <dd className="mb-6">
                            <ul className="list-disc pl-4">
                              {followUpDocumentData?.data?.detail?.followUpTargets?.map(
                                (followUpTarget) => (
                                  <li key={followUpTarget.id}>
                                    <Text
                                      variant="body-sm"
                                      className="line-clamp-3 overflow-wrap-anywhere"
                                    >
                                      {followUpTarget.subIndicator}
                                    </Text>
                                  </li>
                                )
                              )}
                            </ul>
                          </dd>

                          <Text as="dt" variant="body-sm-bold">
                            Kebutuhan Dukungan (Apa/Siapa)
                          </Text>
                          <Text
                            as="dd"
                            variant="body-sm"
                            className="mb-6 line-clamp-3 overflow-wrap-anywhere"
                          >
                            {followUpDocumentData?.data?.detail?.supportNeed ||
                              '-'}
                          </Text>
                        </dl>
                        {!isReadOnly && (
                          <Button
                            size="sm"
                            color="white"
                            aria-haspopup={true}
                            aria-expanded={activeDialog === 'BELAJAR_RGTK'}
                            aria-label="Baca selengkapnya Pilihan Belajar dari Ruang GTK"
                            onClick={() => setActiveDialog('BELAJAR_RGTK')}
                          >
                            Selengkapnya <Icon>chevron_right</Icon>
                          </Button>
                        )}
                      </div>
                      <div className="w-full space-y-4">
                        <fieldset className="space-y-4">
                          <Text variant="heading-sm">
                            {FOLLOW_UP_REFLECTION_CONTENT.inspiration?.question}
                          </Text>
                          <div className="space-y-1">
                            <Text
                              as="label"
                              htmlFor="inspiration"
                              color="subdued"
                              aria-hidden={isReadOnly}
                            >
                              {FOLLOW_UP_REFLECTION_CONTENT.inspiration?.label}
                            </Text>
                            {isReadOnly ? (
                              <>
                                <Text
                                  className="overflow-wrap-anywhere"
                                  aria-hidden="true"
                                >
                                  {formMethods.getValues('inspiration')}
                                </Text>
                                <span className="sr-only">
                                  {`${
                                    FOLLOW_UP_REFLECTION_CONTENT.inspiration
                                      ?.label
                                  }, ${formMethods.getValues('inspiration')}`}
                                </span>
                              </>
                            ) : (
                              <Textarea
                                {...formMethods.register('inspiration', {
                                  required: {
                                    value: true,
                                    message: 'Wajib diisi',
                                  },
                                  maxLength: 500,
                                })}
                                isDisabled={isInputDisabled}
                                id="inspiration"
                                placeholder="Ketik di sini"
                                maxLength={500}
                                isInvalid={
                                  !!formMethods.formState.errors['inspiration']
                                }
                                errorMessage={
                                  formMethods.formState.errors['inspiration']
                                    ?.message
                                }
                              />
                            )}
                          </div>
                        </fieldset>
                        <fieldset className="space-y-4">
                          <Text variant="heading-sm">
                            {FOLLOW_UP_REFLECTION_CONTENT.achievement?.question}
                          </Text>
                          <div className="space-y-1">
                            <Text
                              as="label"
                              htmlFor="achievement"
                              color="subdued"
                              aria-hidden={isReadOnly}
                            >
                              {FOLLOW_UP_REFLECTION_CONTENT.achievement?.label}
                            </Text>
                            {isReadOnly ? (
                              <>
                                <Text
                                  className="overflow-wrap-anywhere"
                                  aria-hidden="true"
                                >
                                  {formMethods.getValues('achievement')}
                                </Text>
                                <span className="sr-only">
                                  {`${
                                    FOLLOW_UP_REFLECTION_CONTENT.achievement
                                      ?.label
                                  }, ${formMethods.getValues('achievement')}`}
                                </span>
                              </>
                            ) : (
                              <Textarea
                                {...formMethods.register('achievement', {
                                  required: {
                                    value: true,
                                    message: 'Wajib diisi',
                                  },
                                  maxLength: 500,
                                })}
                                isDisabled={isInputDisabled}
                                id="achievement"
                                className="w-full"
                                placeholder="Ketik di sini"
                                maxLength={500}
                                isInvalid={
                                  !!formMethods.formState.errors['achievement']
                                }
                                errorMessage={
                                  formMethods.formState.errors['achievement']
                                    ?.message
                                }
                              />
                            )}
                          </div>
                        </fieldset>
                        <fieldset className="space-y-4">
                          <Text variant="heading-sm">
                            {FOLLOW_UP_REFLECTION_CONTENT.challenge?.question}
                          </Text>
                          <div className="space-y-1">
                            <Text
                              as="label"
                              htmlFor="challenge"
                              color="subdued"
                              aria-hidden={isReadOnly}
                            >
                              {FOLLOW_UP_REFLECTION_CONTENT.challenge?.label}
                            </Text>
                            {isReadOnly ? (
                              <>
                                <Text
                                  className="overflow-wrap-anywhere"
                                  aria-hidden="true"
                                >
                                  {formMethods.getValues('challenge')}
                                </Text>
                                <span className="sr-only">
                                  {`${
                                    FOLLOW_UP_REFLECTION_CONTENT.challenge
                                      ?.label
                                  }, ${formMethods.getValues('challenge')}`}
                                </span>
                              </>
                            ) : (
                              <Textarea
                                {...formMethods.register('challenge', {
                                  required: {
                                    value: true,
                                    message: 'Wajib diisi',
                                  },
                                  maxLength: 500,
                                })}
                                isDisabled={isInputDisabled}
                                id="challenge"
                                placeholder="Ketik di sini"
                                maxLength={500}
                                isInvalid={
                                  !!formMethods.formState.errors['challenge']
                                }
                                errorMessage={
                                  formMethods.formState.errors['challenge']
                                    ?.message
                                }
                              />
                            )}
                          </div>
                        </fieldset>
                        <fieldset className="space-y-4">
                          <Text variant="heading-sm">
                            {
                              FOLLOW_UP_REFLECTION_CONTENT.improvementPlan
                                ?.question
                            }
                          </Text>
                          <div className="space-y-1">
                            <Text
                              as="label"
                              htmlFor="improvementPlan"
                              color="subdued"
                              aria-hidden={isReadOnly}
                            >
                              {
                                FOLLOW_UP_REFLECTION_CONTENT.improvementPlan
                                  ?.label
                              }
                            </Text>
                            {isReadOnly ? (
                              <>
                                <Text
                                  className="overflow-wrap-anywhere"
                                  aria-hidden="true"
                                >
                                  {formMethods.getValues('improvementPlan')}
                                </Text>
                                <span className="sr-only">
                                  {`${
                                    FOLLOW_UP_REFLECTION_CONTENT.improvementPlan
                                      ?.label
                                  }, ${formMethods.getValues(
                                    'improvementPlan'
                                  )}`}
                                </span>
                              </>
                            ) : (
                              <Textarea
                                {...formMethods.register('improvementPlan', {
                                  required: {
                                    value: true,
                                    message: 'Wajib diisi',
                                  },
                                  maxLength: 500,
                                })}
                                isDisabled={isInputDisabled}
                                id="improvementPlan"
                                placeholder="Ketik di sini"
                                maxLength={500}
                                isInvalid={
                                  !!formMethods.formState.errors[
                                    'improvementPlan'
                                  ]
                                }
                                errorMessage={
                                  formMethods.formState.errors[
                                    'improvementPlan'
                                  ]?.message
                                }
                              />
                            )}
                          </div>
                        </fieldset>
                      </div>
                    </div>
                  </div>
                )}
              </SectionCardBody>

              {!isInputDisabled && (
                <SectionCardFooter className="justify-end px-6 py-6 space-x-3">
                  <Button
                    size="lg"
                    color="white"
                    type="button"
                    onClick={handleClickSaveDraftButton}
                  >
                    Simpan draf
                  </Button>
                  <Button size="lg" color="blue" type="submit">
                    Kirim
                  </Button>
                </SectionCardFooter>
              )}
            </SectionCard>

            <DetailDialog
              followUpEffort={
                followUpDocumentData?.data?.detail?.additionalFollowUpEffort
              }
              followUpPeriod={
                followUpDocumentData?.data?.detail?.additionalFollowUpPeriod
              }
              followUpTarget={
                followUpDocumentData?.data?.detail?.additionalFollowUpTarget
              }
              isOpen={activeDialog === 'BELAJAR_LAINNYA'}
              supportNeed={validatedAdditionalSupportNeed}
              title="Pilihan Belajar"
              onClose={() => setActiveDialog('NONE')}
            />

            <DetailDialog
              followUpTargets={
                followUpDocumentData?.data?.detail?.followUpTargets
              }
              isOpen={activeDialog === 'BELAJAR_RGTK'}
              supportNeed={
                followUpDocumentData?.data?.detail?.supportNeed || '-'
              }
              title="Pilihan Belajar dari Ruang GTK"
              onClose={() => setActiveDialog('NONE')}
            />

            <SubmitConfirmationDialog
              isOpen={activeDialog === 'SUBMIT'}
              onCancel={() => setActiveDialog('NONE')}
              isLoading={upsertMutation.isLoading}
              onConfirm={handleClickConfirmSubmitDialog}
            />

            <SaveToDraftDialog
              isOpen={activeDialog === 'SAVE_TO_DRAFT'}
              isLoading={upsertMutation.isLoading}
              onCancel={() => setActiveDialog('NONE')}
              onConfirm={() => handleUpsert('DRAFT')}
            />
          </form>
        )}
      </PageGuardGpkPelaksanaanStatus>
    </div>
  )
}

type DetailDialogProps = Omit<DialogProps, 'children'> & {
  title: string
  followUpEffort?: string
  followUpPeriod?: string
  followUpTarget?: string
  followUpTargets?: Array<IFollowUpTargetItem>
  supportNeed: string
}

export function DetailDialog({
  followUpEffort,
  followUpPeriod,
  followUpTarget,
  followUpTargets,
  supportNeed,
  title,
  ...dialogProps
}: DetailDialogProps) {
  return (
    <Dialog size="md" {...dialogProps}>
      <DialogOverlay />
      <DialogContent>
        <DialogHeader title={title} />

        <div className="flex flex-col p-6 space-y-4">
          <dl className="border rounded border-disabled">
            {followUpTargets && followUpTargets.length > 0 ? (
              <div className="p-3 border-b border-disabled last:border-0">
                <Text as="dt">Tujuan Tindak Lanjut</Text>
                <dd className="mt-2">
                  <ul className="list-disc pl-4">
                    {followUpTargets.map((followUpTarget) => (
                      <li key={followUpTarget.id}>
                        <Text
                          className="overflow-wrap-anywhere"
                          color="subdued"
                          variant="body-sm"
                        >
                          {followUpTarget.subIndicator}
                        </Text>
                      </li>
                    ))}
                  </ul>
                </dd>
              </div>
            ) : (
              followUpTarget && (
                <div className="p-3 border-b border-disabled last:border-0">
                  <Text as="dt">Tujuan Tindak Lanjut</Text>
                  <Text
                    as="dd"
                    className="overflow-wrap-anywhere"
                    color="subdued"
                    variant="body-sm"
                  >
                    {followUpTarget || '-'}
                  </Text>
                </div>
              )
            )}
            {followUpEffort && (
              <div className="p-3 border-b border-disabled last:border-0">
                <Text as="dt">Upaya Tindak Lanjut</Text>
                <Text
                  as="dd"
                  className="overflow-wrap-anywhere"
                  color="subdued"
                  variant="body-sm"
                >
                  {followUpEffort || '-'}
                </Text>
              </div>
            )}
            {followUpPeriod && (
              <div className="p-3 border-b border-disabled last:border-0">
                <Text as="dt">Periode Tindak Lanjut</Text>
                <Text
                  as="dd"
                  className="overflow-wrap-anywhere"
                  color="subdued"
                  variant="body-sm"
                >
                  {followUpPeriod}
                </Text>
              </div>
            )}
            <div className="p-3 border-b border-disabled last:border-0">
              <Text as="dt">Kebutuhan Dukungan (Apa/Siapa)</Text>
              <Text
                as="dd"
                className="overflow-wrap-anywhere"
                color="subdued"
                variant="body-sm"
              >
                {supportNeed || '-'}
              </Text>
            </div>
          </dl>

          <div className="flex items-center justify-center">
            <Button onClick={() => dialogProps?.onClose?.()}>Kembali</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
